<template>
  <v-container>
    <v-row>
      <v-select
        v-model="select"
        item-value="id"
        :items="items"
        :label="$t('component.period_field.title')"
        :rounded="rounded"
      >
        <template v-slot:selection="{ item }">
          {{ item.text }}
        </template>
        <template v-slot:item="{ item }">
          {{ item.text }}
        </template>
      </v-select>
    </v-row>

    <v-row>
      <template
        v-if="!resetPickers && select !== null && (items[select].range === 'one' || items[select].range === 'range' || items[select].range === 'rangetime')">
        <v-col class="px-0">
          <v-menu
            ref="oneMenu"
            v-model="oneMenu"
            :close-on-content-click="false"
            :return-value.sync="oneMenu"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(oneDate)"
                :label="items[select].range === 'one' ? items[select].text : $t('component.period_field.from')"
                readonly
                v-bind="attrs"
                v-on="on"
                :rounded="rounded"
              ></v-text-field>
            </template>
            <v-date-picker v-model="oneDate"
                           :type="dateType"
                           :max="getCurrenDateInMS()"
                           no-title
                           scrollable
                           :first-day-of-week="1"
                           :locale="getIsoLocale()"
                           @input="oneMenu = false"
            >
            </v-date-picker>
          </v-menu>
        </v-col>

        <v-col class="px-0" v-if="items[select].range === 'rangetime'">
          <v-menu
            ref="oneMenuTime"
            v-model="oneMenuTime"
            :close-on-content-click="false"
            :return-value.sync="oneMenuTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="oneTime"
                :label="items[select].range === 'one' ? items[select].text : $t('component.period_field.time')"
                readonly
                v-bind="attrs"
                v-on="on"
                :rounded="rounded"
              ></v-text-field>
            </template>
            <v-time-picker ref="oneDateTime"
                           v-model="oneTime"
                           format="24hr"
                           scrollable
                           @click:minute="$refs.oneMenuTime.save(oneTime)"
            >
            </v-time-picker>
          </v-menu>
        </v-col>
      </template>
    </v-row>

    <v-row>
      <template
        v-if="!resetPickers && select !== null && (items[select].range === 'range' || items[select].range === 'rangetime')">
        <v-col class="px-0">
          <v-menu
            ref="rangeDate"
            v-model="rangeMenu"
            :close-on-content-click="false"
            :return-value.sync="rangeMenu"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formatDate(rangeDate)"
                :label="$t('component.period_field.to')"
                readonly
                v-bind="attrs"
                v-on="on"
                :rounded="rounded"
              ></v-text-field>
            </template>
            <v-date-picker v-model="rangeDate"
                           :type="dateType"
                           :max="getCurrenDateInMS()"
                           no-title
                           scrollable
                           :first-day-of-week="1"
                           :locale="getIsoLocale()"
                           @input="rangeMenu = false"
            >
            </v-date-picker>
          </v-menu>

        </v-col>

        <v-col class="px-0" v-if="items[select].range === 'rangetime'">
          <v-menu
            ref="rangeMenuTime"
            v-model="rangeMenuTime"
            :close-on-content-click="false"
            :return-value.sync="rangeMenuTime"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="rangeTime"
                :label="items[select].range === 'one' ? items[select].text : $t('component.period_field.time')"
                readonly
                v-bind="attrs"
                v-on="on"
                :rounded="rounded"
              ></v-text-field>
            </template>
            <v-time-picker ref="rangeDateTime"
                           v-model="rangeTime"
                           format="24hr"
                           scrollable
                           @click:minute="$refs.rangeMenuTime.save(rangeTime)"
            >
            </v-time-picker>
          </v-menu>
        </v-col>
      </template>
    </v-row>
  </v-container>
</template>

<script>
/*
 * Parametr definic obdobi:
 * [
 *    {
 *      name = "day|month + current|previous|one|range|rangetime"
 *      text = volitelný text
 *    }
 * ]
 *
 * nodel input value format:
 * {
 *    name = "day|month + current|previous|one|range|rangetime"
 *    from: "2022-10-10"
 *    to: "2022-10-10"
 * }
 */
import i18n from "@/plugins/i18n";
import { DateTime } from "luxon";

export default {
  name: "PeriodField",
  props: {
    periods: {
      type: Array,
      default: null,
      mandatory: true
    },
    value: {
      type: Object
    },
    rounded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      items: null,
      select: null,
      oneMenu: false,
      oneDate: null,
      oneMenuTime: false,
      oneTime: null,
      rangeMenu: false,
      rangeDate: null,
      rangeMenuTime: false,
      rangeTime: null,
      disableEmit: false,
      dateType: "month",
      resetPickers: false
    };
  },
  methods: {
    getIsoLocale() {
      return i18n.messages[i18n.locale].lang.iso;
    },

    getValue() {
      let value = null;

      if (this.select !== null) {
        const def = this.items[this.select];
        const now = DateTime.now();

        if (def.type === "day") {
          if (def.range === "current") {
            const date = now.toFormat("yyyy-MM-dd");
            value = { name: def.name, from: date, to: date, text: date };
          } else if (def.range === "previous") {
            const date = now.minus({ days: 1 }).toFormat("yyyy-MM-dd");
            value = { name: def.name, from: date, to: date, text: date };
          } else if (def.range === "one" && this.oneDate !== null) {
            const date = DateTime.fromISO(this.oneDate).toFormat("yyyy-MM-dd");
            value = { name: def.name, from: date, to: date, text: date };
          } else if (def.range === "range" && this.oneDate !== null && this.rangeDate !== null) {
            const date_from = DateTime.fromISO(this.oneDate).toFormat("yyyy-MM-dd");
            const date_to = DateTime.fromISO(this.rangeDate).toFormat("yyyy-MM-dd");
            value = { name: def.name, from: date_from, to: date_to, text: date_from + "_" + date_to };
          } else if (def.range === "rangetime" && this.oneDate !== null && this.rangeDate !== null) {
            const date_from = DateTime.fromISO(this.oneDate).toFormat("yyyy-MM-dd");
            const date_to = DateTime.fromISO(this.rangeDate).toFormat("yyyy-MM-dd");
            const time_from = (this.oneTime !== null) ? this.oneTime : "00:00";
            const time_to = (this.rangeTime !== null) ? this.rangeTime : "23:59";
            value = {
              name: def.name, from: date_from, to: date_to,
              fromTime: time_from, toTime: time_to,
              text: date_from + "@" + time_from.replace(":", "") + "_" + date_to + "@" + time_to.replace(":", "")
            };
          }
        } else if (def.type === "month") {
          if (def.range === "current") {
            const date_from = now.startOf("month").toFormat("yyyy-MM-dd");
            const date_to = now.endOf("month").toFormat("yyyy-MM-dd");
            value = { name: def.name, from: date_from, to: date_to, text: now.startOf("month").toFormat("yyyy-MM") };
          } else if (def.range === "previous") {
            const date = now.minus({ months: 1 });
            const date_from = date.startOf("month").toFormat("yyyy-MM-dd");
            const date_to = date.endOf("month").toFormat("yyyy-MM-dd");
            value = { name: def.name, from: date_from, to: date_to, text: date.startOf("month").toFormat("yyyy-MM") };
          } else if (def.range === "one" && this.oneDate !== null) {
            const date_from = DateTime.fromISO(this.oneDate).startOf("month").toFormat("yyyy-MM-dd");
            const date_to = DateTime.fromISO(this.oneDate).endOf("month").toFormat("yyyy-MM-dd");
            value = {
              name: def.name,
              from: date_from,
              to: date_to,
              text: DateTime.fromISO(this.oneDate).toFormat("yyyy-MM")
            };
          } else if (def.range === "range" && this.oneDate !== null && this.rangeDate !== null) {
            const date_from = DateTime.fromISO(this.oneDate).startOf("month").toFormat("yyyy-MM-dd");
            const date_to = DateTime.fromISO(this.rangeDate).endOf("month").toFormat("yyyy-MM-dd");
            value = {
              name: def.name, from: date_from, to: date_to,
              text: DateTime.fromISO(this.oneDate).toFormat("yyyy-MM") + "_" + DateTime.fromISO(this.rangeDate).toFormat("yyyy-MM")
            };
          }
        }
      }

      return value;
    },

    emitValue() {
      if (this.disableEmit)
        return;

      this.$emit("input", this.getValue());
    },

    getText(item) {
      return item.text || this.$t("component.period_field." + item.name);
    },

    getPickerType(item) {
      if (item.type === "month")
        return "month";
      else
        return "date";
    },

    getCurrenDateInMS() {
      return DateTime.now().startOf("day").plus({ days: 1 }).toISO();
    },

    periodsToItems() {
      let id = 0;
      this.items = this.periods.map(x => {
        const p = x.name.split("-");
        return { id: id++, name: x.name, type: p[0], range: p[1], text: this.getText(x) };
      });
    },

    setValue(value, emit = true) {
      value = value || {};
      const def = this.items.find(x => x.name === value.name);
      const currentValue = this.getValue();

      if (def !== null && def !== undefined
        && (this.select !== def.id
          || (currentValue.from || null) !== (value.from || null)
          || (currentValue.to || null) !== (value.to || null)
          || (currentValue.fromTime || null) !== (value.fromTime || null)
          || (currentValue.toTime || null) !== (value.toTime || null))) {
        this.disableEmit = true;
        this.select = def.id;
        this.$nextTick().then(() => {
          this.oneDate = value.from || null;
          this.rangeDate = value.to || null;
          this.oneTime = value.fromTime || null;
          this.rangeTime = value.toTime || null;

          this.$nextTick().then(() => {
            this.disableEmit = false;
            if (emit)
              this.emitValue();
          });
        });
      }
    },

    formatDate(date) {
      if (date === null)
        return null;

      const def = this.items[this.select];

      if (def.type === "day")
        return DateTime.fromISO(date).toLocaleString("ll");
      else
        return DateTime.fromISO(date).toLocaleString({ month: "long", year: "numeric" });
    }
  },

  watch: {
    periods: function() {
      this.periodsToItems();
    },

    value: function() {
      this.setValue(this.value, true);
    },

    select: function() {
      this.resetPickers = true;
      this.oneMenu = false;
      this.oneDate = null;
      this.rangeMenu = false;
      this.rangeDate = null;

      this.$nextTick().then(() => {
        this.resetPickers = false;
        this.dateType = this.getPickerType(this.items[this.select]);
        this.emitValue();
      });
    },

    oneDate: function() {
      this.emitValue();
    },

    rangeDate: function() {
      this.emitValue();
    },

    oneTime: function() {
      this.emitValue();
    },

    rangeTime: function() {
      this.emitValue();
    }
  },

  mounted() {
    this.periodsToItems();

    this.setValue(this.value);
  }
};
</script>

<style scoped>

</style>